import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide } from '@mui/material';

/**
 * Toast Notification Context
 *
 * Provides toast/snackbar notifications throughout the app
 * Replaces alert() calls with elegant, non-blocking notifications
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Operation completed!');
 *   toast.error('Something went wrong');
 *   toast.info('Here is some information');
 *   toast.warning('Be careful!');
 */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toastState, setToastState] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success' | 'error' | 'warning' | 'info'
    duration: 4000
  });

  const showToast = useCallback((message, severity = 'info', duration = 4000) => {
    setToastState({
      open: true,
      message,
      severity,
      duration
    });
  }, []);

  const hideToast = useCallback(() => {
    setToastState(prev => ({ ...prev, open: false }));
  }, []);

  const toast = {
    success: useCallback((message, duration) => showToast(message, 'success', duration), [showToast]),
    error: useCallback((message, duration) => showToast(message, 'error', duration), [showToast]),
    warning: useCallback((message, duration) => showToast(message, 'warning', duration), [showToast]),
    info: useCallback((message, duration) => showToast(message, 'info', duration), [showToast]),
    show: showToast
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <Snackbar
        open={toastState.open}
        autoHideDuration={toastState.duration}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        sx={{ bottom: { xs: 80, sm: 24 } }} // Avoid mobile bottom navigation
      >
        <Alert
          onClose={hideToast}
          severity={toastState.severity}
          variant="filled"
          sx={{
            width: '100%',
            boxShadow: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export default ToastContext;
